import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Plus, Trash2 } from "lucide-react";
import { api, downloadFile, fmtDate } from "@/lib/api";

const TABS = [
  { key: "interest", label: "Interest lists" },
  { key: "checkups", label: "Document checkups" },
  { key: "registrations", label: "Workshop registrations" },
];

const TYPE_LABEL = { drug_testing: "Drug Testing", fingerprinting: "Fingerprinting" };

export const AdminLeads = () => {
  const [tab, setTab] = useState("interest");
  const [data, setData] = useState(null);

  const load = (t = tab) => {
    setData(null);
    const url =
      t === "interest" ? "/api/admin/interest" : t === "checkups" ? "/api/admin/checkups" : "/api/admin/workshop-registrations";
    api.get(url).then((r) => setData(r.data)).catch(() => setData([]));
  };
  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line

  const exportUrl =
    tab === "interest"
      ? "/api/admin/interest/export"
      : tab === "checkups"
        ? "/api/admin/checkups/export"
        : "/api/admin/workshop-registrations/export";
  const exportFile =
    tab === "interest" ? "TSM4_interest_list.csv" : tab === "checkups" ? "TSM_document_checkups.csv" : "TSM_workshop_registrations.csv";

  return (
    <div className="space-y-6" data-testid="admin-leads">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-extrabold text-navy">Leads &amp; interest</h1>
        <button
          type="button"
          onClick={() => downloadFile(exportUrl, exportFile)}
          data-testid="admin-leads-export"
          className="inline-flex items-center gap-2 rounded-sm border border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-gold"
        >
          <Download className="h-4 w-4" />
          Download CSV
        </button>
      </div>

      <div className="flex gap-2" data-testid="admin-leads-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`admin-leads-tab-${t.key}`}
            className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === t.key ? "border-teal bg-teal text-white" : "border-navy/15 bg-white text-navy/60 hover:border-teal/50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="flex h-48 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : data.length === 0 ? (
        <div className="rounded-lg border border-dashed border-navy/25 bg-white px-8 py-14 text-center" data-testid="admin-leads-empty">
          <p className="font-semibold text-navy">Nothing here yet.</p>
          <p className="mt-2 text-sm text-ink/55">
            {tab === "interest" && "Website interest-list signups (drug testing / fingerprinting) will appear here."}
            {tab === "checkups" && "Complimentary Business Document Checkup requests will appear here."}
            {tab === "registrations" && "Workshop registrations will appear here once events are scheduled."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-navy/10 bg-white">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
                {tab === "interest" && ["Type", "Contact", "Business", "Email", "Phone", "Needs", "Submitted"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
                {tab === "checkups" && ["Contact", "Business", "Email", "Phone", "Focus", "Submitted"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
                {tab === "registrations" && ["Workshop", "Contact", "Email", "Phone", "Registered"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => (
                <tr key={d.id || i} className="border-b border-navy/5 align-top" data-testid={`admin-lead-row-${i}`}>
                  {tab === "interest" && (
                    <>
                      <td className="px-4 py-3"><span className="rounded-full border border-teal/30 bg-teal/5 px-2.5 py-1 text-xs font-semibold text-tealdeep">{TYPE_LABEL[d.interest_type] || d.interest_type}</span></td>
                      <td className="px-4 py-3 font-medium text-navy">{d.full_name}</td>
                      <td className="px-4 py-3 text-ink/60">{d.business_name || "—"}</td>
                      <td className="px-4 py-3 text-ink/60">{d.email}</td>
                      <td className="px-4 py-3 text-ink/60">{d.phone}</td>
                      <td className="max-w-xs px-4 py-3 text-ink/60">{(d.needs || "").slice(0, 120) || "—"}</td>
                      <td className="px-4 py-3 text-ink/50">{fmtDate(d.created_at)}</td>
                    </>
                  )}
                  {tab === "checkups" && (
                    <>
                      <td className="px-4 py-3 font-medium text-navy">{d.full_name}</td>
                      <td className="px-4 py-3 text-ink/60">{d.business_name || "—"}</td>
                      <td className="px-4 py-3 text-ink/60">{d.email}</td>
                      <td className="px-4 py-3 text-ink/60">{d.phone}</td>
                      <td className="max-w-xs px-4 py-3 text-ink/60">{(d.focus || "").slice(0, 120) || "—"}</td>
                      <td className="px-4 py-3 text-ink/50">{fmtDate(d.created_at)}</td>
                    </>
                  )}
                  {tab === "registrations" && (
                    <>
                      <td className="px-4 py-3 font-medium text-navy">{d.workshop_title}</td>
                      <td className="px-4 py-3 text-ink/60">{d.full_name}</td>
                      <td className="px-4 py-3 text-ink/60">{d.email}</td>
                      <td className="px-4 py-3 text-ink/60">{d.phone || "—"}</td>
                      <td className="px-4 py-3 text-ink/50">{fmtDate(d.created_at)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EMPTY_WS = { title: "", description: "", starts_at: "", location: "", registration_open: true, published: true };

export const AdminWorkshops = () => {
  const [workshops, setWorkshops] = useState(null);
  const [editing, setEditing] = useState(null); // null | {id?, ...fields}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/api/admin/workshops").then((r) => setWorkshops(r.data)).catch(() => setWorkshops([]));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (editing.id) {
        await api.put(`/api/admin/workshops/${editing.id}`, editing);
      } else {
        await api.post("/api/admin/workshops", editing);
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Saving failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete workshop "${title}"? Registrations are kept.`)) return;
    await api.delete(`/api/admin/workshops/${id}`);
    load();
  };

  if (!workshops) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const f = "w-full rounded-sm border border-navy/15 bg-white px-3 py-2 text-sm text-navy focus:border-teal focus:outline-none";
  const l = "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/60";

  return (
    <div className="space-y-6" data-testid="admin-workshops">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-navy">Workshops</h1>
          <p className="mt-2 text-sm text-ink/60">Published, upcoming workshops appear on the Workshops &amp; Resources page with registration.</p>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={() => downloadFile("/api/admin/workshop-registrations/export", "TSM_workshop_registrations.csv")} data-testid="admin-workshops-export" className="inline-flex items-center gap-2 rounded-sm border border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-gold">
            <Download className="h-4 w-4" /> Registrations CSV
          </button>
          <button type="button" onClick={() => setEditing({ ...EMPTY_WS })} data-testid="admin-workshop-new" className="btn-gold !px-5 !py-2.5 text-xs">
            <Plus className="h-4 w-4" /> New workshop
          </button>
        </div>
      </div>

      {error && <p className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-workshops-error">{error}</p>}

      {editing && (
        <form onSubmit={save} className="rounded-lg border border-teal/30 bg-white p-6" data-testid="admin-workshop-form">
          <h2 className="text-lg font-extrabold text-navy">{editing.id ? "Edit workshop" : "New workshop"}</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={l} htmlFor="ws-title">Title *</label>
              <input id="ws-title" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={f} data-testid="admin-workshop-title" />
            </div>
            <div className="sm:col-span-2">
              <label className={l} htmlFor="ws-desc">Description</label>
              <textarea id="ws-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={`${f} min-h-20`} data-testid="admin-workshop-description" />
            </div>
            <div>
              <label className={l} htmlFor="ws-date">Starts at *</label>
              <input id="ws-date" type="datetime-local" required value={editing.starts_at} onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })} className={f} data-testid="admin-workshop-date" />
            </div>
            <div>
              <label className={l} htmlFor="ws-loc">Location (e.g., "Warrensville Heights, OH" or "Virtual")</label>
              <input id="ws-loc" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className={f} data-testid="admin-workshop-location" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-navy/80">
              <input type="checkbox" checked={editing.registration_open} onChange={(e) => setEditing({ ...editing, registration_open: e.target.checked })} data-testid="admin-workshop-regopen" /> Registration open
            </label>
            <label className="flex items-center gap-2 text-sm text-navy/80">
              <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="admin-workshop-published" /> Published
            </label>
          </div>
          <div className="mt-6 flex gap-3">
            <button type="submit" disabled={busy} data-testid="admin-workshop-save" className="btn-gold !px-6 !py-2.5 text-xs">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save workshop</button>
            <button type="button" onClick={() => setEditing(null)} data-testid="admin-workshop-cancel" className="btn-outline !px-6 !py-2.5 text-xs">Cancel</button>
          </div>
          <p className="mt-3 text-xs italic text-ink/50">Enter the date/time in your local timezone — it is stored as-is and displayed to visitors the same way.</p>
        </form>
      )}

      <div className="overflow-x-auto rounded-lg border border-navy/10 bg-white">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead>
            <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
              {["Workshop", "Starts at", "Location", "Status", "Actions"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {workshops.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-ink/50">No workshops yet — create your first one when you have a date.</td></tr>}
            {workshops.map((w) => (
              <tr key={w._id} className="border-b border-navy/5" data-testid={`admin-workshop-row-${w._id}`}>
                <td className="px-4 py-3 font-semibold text-navy">{w.title}</td>
                <td className="px-4 py-3 text-ink/60">{fmtDate(w.starts_at)}</td>
                <td className="px-4 py-3 text-ink/60">{w.location || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${w.published ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-navy/10 bg-navy/5 text-navy/50"}`}>
                    {w.published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setEditing({ id: w._id, title: w.title, description: w.description || "", starts_at: (w.starts_at || "").slice(0, 16), location: w.location || "", registration_open: !!w.registration_open, published: !!w.published })} data-testid={`admin-workshop-edit-${w._id}`} className="rounded-sm border border-navy/15 px-2.5 py-1.5 text-xs font-semibold text-navy hover:border-gold">Edit</button>
                    <button type="button" onClick={() => remove(w._id, w.title)} data-testid={`admin-workshop-delete-${w._id}`} className="rounded-sm border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Link to="/admin" className="inline-block text-sm font-semibold text-golddark hover:text-navy" data-testid="admin-workshops-back">← Back to overview</Link>
    </div>
  );
};
